import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { format, parse } from "date-fns"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Parse a date string as LOCAL time to avoid UTC->local day shifts.
 * A DB value like "2026-05-30" (or "2026-05-30T00:00:00Z") parsed with
 * `new Date(str)` becomes UTC midnight, which renders as May 29 in
 * negative-UTC-offset timezones (Americas). This helper keeps the
 * calendar day intact regardless of the viewer's timezone.
 */
export function parseLocalDate(input: string | Date | null | undefined): Date | null {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  const match = String(input).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, y, m, d] = match;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }
  const dm = String(input).match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (dm) {
    const [, d, m, y] = dm;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }
  const fallback = new Date(input);
  return isNaN(fallback.getTime()) ? null : fallback;
}

/**
 * Get day name from a date string (handles multiple formats)
 * @param dateString - Date in format DD/MM/YYYY, YYYY-MM-DD, or a day name
 * @returns Day name (e.g., "Monday", "Tuesday")
 */
export function getDayName(dateString: string | Date): string {
  if (!dateString) return '';
  
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  // If it's already a day name, return it
  if (typeof dateString === 'string' && dayNames.includes(dateString)) {
    return dateString;
  }
  
  try {
    let date: Date;
    if (typeof dateString === 'string') {
      // Try ISO format first (YYYY-MM-DD)
      if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
        date = new Date(dateString);
      } 
      // Try European format (DD/MM/YYYY)
      else if (/^\d{2}\/\d{2}\/\d{4}/.test(dateString)) {
        date = parse(dateString, 'dd/MM/yyyy', new Date());
      }
      else {
        return dateString; // Return as-is if format unknown
      }
    } else {
      date = dateString;
    }
    
    if (isNaN(date.getTime())) {
      return dateString?.toString() || '';
    }
    
    return format(date, 'EEEE');
  } catch (e) {
    return dateString?.toString() || '';
  }
}

/**
 * Format a date string to show both day name and date (e.g., "Monday, May 17")
 * @param dateString - Date in format DD/MM/YYYY, YYYY-MM-DD, or a day name
 * @returns Formatted string like "Monday, May 17" or just the day name if no date
 */
export function formatDayWithDate(dateString: string | Date): string {
  if (!dateString) return '';
  
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  // If it's already just a day name, return it
  if (typeof dateString === 'string' && dayNames.includes(dateString)) {
    return dateString;
  }
  
  try {
    let date: Date;
    if (typeof dateString === 'string') {
      // Try ISO format first (YYYY-MM-DD) - parse as local time to avoid UTC shift
      if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
        const [year, month, day] = dateString.split(/[-T]/);
        date = new Date(Number(year), Number(month) - 1, Number(day));
      }
      // Try European format (DD/MM/YYYY)
      else if (/^\d{2}\/\d{2}\/\d{4}/.test(dateString)) {
        date = parse(dateString, 'dd/MM/yyyy', new Date());
      }
      else {
        return dateString; // Return as-is if format unknown
      }
    } else {
      date = dateString;
    }
    
    if (isNaN(date.getTime())) {
      return dateString?.toString() || '';
    }
    
    // Return "Monday, May 17" format
    return format(date, 'EEEE, MMM d');
  } catch (e) {
    return dateString?.toString() || '';
  }
}

/**
 * Format date for display
 * @param dateString - Date in format DD/MM/YYYY
 * @returns Formatted date (e.g., "Monday, Dec 2")
 */
export function formatDisplayDate(dateString: string): string {
  try {
    const date = parse(dateString, 'dd/MM/yyyy', new Date());
    return format(date, 'EEEE, MMM d');
  } catch (e) {
    console.error('Error formatting date:', e);
    return dateString;
  }
}

/**
 * Generate day options based on event start and end dates
 * @param startDate - Event start date
 * @param endDate - Event end date
 * @returns Array of day label options
 */
export function generateDayOptions(startDate: Date, endDate: Date): string[] {
  const options: string[] = [];
  const currentDate = new Date(startDate);
  
  while (currentDate <= endDate) {
    const dayName = format(currentDate, 'EEEE');
    options.push(dayName);
    options.push(`${dayName} Morning`);
    options.push(`${dayName} Afternoon`);
    options.push(`${dayName} Evening`);
    currentDate.setDate(currentDate.getDate() + 1);
  }
  
  return options;
}

/**
 * Strip HTML tags from a string
 * @param html - String containing HTML
 * @returns Plain text without HTML tags
 */
export function stripHtml(html: string): string {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .trim();
}

/**
 * Map Tilda category names to system categories
 * @param tildaCategory - Category name from Tilda
 * @returns Valid system category
 */
export function mapTildaCategory(tildaCategory: string): string {
  if (!tildaCategory) return 'ticket';
  
  const category = tildaCategory.toLowerCase().trim();
  
  // Festival passes
  if (category.includes('fest') || category.includes('festival')) {
    return 'festival_pass';
  }
  
  // Workshops and bootcamps
  if (category.includes('bootcamp') || category.includes('workshop') || category.includes('course')) {
    return 'workshop';
  }
  
  // Accommodation
  if (category.includes('accommodation') || category.includes('lodging') || category.includes('room')) {
    return 'accommodation';
  }
  
  // Food
  if (category.includes('food') || category.includes('meal') || category.includes('catering')) {
    return 'food';
  }
  
  // Transport
  if (category.includes('transport') || category.includes('shuttle') || category.includes('transfer')) {
    return 'transport';
  }
  
  // Merchandise
  if (category.includes('merch') || category.includes('clothing') || category.includes('gear')) {
    return 'merchandise';
  }
  
  // Default to ticket
  return 'ticket';
}
